import React, { useState, useEffect, useContext } from "react";
import { AppStateContext } from "../../state/AppProvider";
import { AnimatePresence, motion } from "framer-motion";
import { Spinner, ISpinnerStyles } from "@fluentui/react";
import { ButtonTooltip } from "../ButtonTooltip";
import { Country } from "../../api/models";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

interface Props {
    onSend: (question: string) => void;
    disabled: boolean;
    clearOnSend?: boolean;
    chatEmpty: boolean;
    windowWidth: number;
    fileInputRef?: React.RefObject<HTMLInputElement>;
    uploadFile?: () => void;
    showToEnd?: boolean;
    toEnd?: () => void;
}

export const FormInput = ({
    onSend,
    disabled,
    clearOnSend,
    chatEmpty,
    windowWidth,
    fileInputRef,
    uploadFile,
    showToEnd,
    toEnd
}: Props) => {
    const appStateContext = useContext(AppStateContext);

    const [fullName, setFullName] = useState("");
    const [gender, setGender] = useState("");
    const [dob, setDob] = useState("");
    const [countryLocation, setCountryLocation] = useState("");
    const [placeOfBirth, setPlaceOfBirth] = useState("");
    const [nationalityIso3, setNationalityIso3] = useState("");
    const [regCountryOrg, setRegCountryOrg] = useState("");
    const [imoNumber, setImoNumber] = useState("");
    const [mediaCheck, setMediaCheck] = useState(false); // add media check toggle

    const [isoCountries, setIsoCountries] = useState<Country[]>([]);

    useEffect(() => {
        countries.registerLocale(enLocale);
        const names = countries.getNames("en", { select: "official" }) as Record<string, string>;
        const mapped: Country[] = Object.entries(names)
            .map(([alpha2, name]) => {
                const alpha3 = countries.alpha2ToAlpha3(alpha2);
                return alpha3 ? { code: alpha3, name } : null;
            })
            .filter((x): x is Country => !!x)
            .sort((a, b) => a.name.localeCompare(b.name));
        setIsoCountries(mapped);
    }, []);

    const placeholders = appStateContext?.state.assistant?.examples || [];
    const [currentPlaceholder, setCurrentPlaceholder] = useState(0);
    useEffect(() => {
        if (!placeholders.length) return;
        const id = setInterval(() => {
            setCurrentPlaceholder(p => (p + 1) % placeholders.length);
        }, 3000);
        return () => clearInterval(id);
    }, [placeholders.length]);

    const buildMessage = () => {
        const lines: string[] = [];
        if (fullName.trim()) lines.push(`Full name: ${fullName.trim()}`);
        if (gender.trim()) lines.push(`Gender: ${gender.trim().toUpperCase()}`);
        if (dob.trim()) lines.push(`Date of birth: ${dob.trim()}`);
        if (countryLocation.trim()) lines.push(`Country location (ISO3): ${countryLocation.trim()}`);
        if (placeOfBirth.trim()) lines.push(`Place of birth (ISO3): ${placeOfBirth.trim()}`);
        if (nationalityIso3.trim()) lines.push(`Nationality (ISO3): ${nationalityIso3.trim()}`);
        if (regCountryOrg.trim()) lines.push(`Registered country (organisation): ${regCountryOrg.trim()}`);
        if (imoNumber.trim()) lines.push(`IMO number (vessel): ${imoNumber.trim()}`);
        lines.push(mediaCheck ? "Providers: WATCHLIST, MEDIA_CHECK" : "Providers: WATCHLIST"); // add providers based on checkbox
        return lines.join("\n");
    };

    const formEmpty =
        !fullName.trim() &&
        !gender.trim() &&
        !dob.trim() &&
        !countryLocation.trim() &&
        !placeOfBirth.trim() &&
        !nationalityIso3.trim() &&
        !regCountryOrg.trim() &&
        !imoNumber.trim();
    const sendDisabled = disabled || formEmpty;

    const resetForm = () => {
        setFullName("");
        setGender("");
        setDob("");
        setCountryLocation("");
        setPlaceOfBirth("");
        setNationalityIso3("");
        setRegCountryOrg("");
        setImoNumber("");
        setMediaCheck(false); // reset media check
    };

    const sendForm = () => {
        if (sendDisabled) return;
        onSend(buildMessage());
        if (clearOnSend) resetForm();
    };

    const onEnterPress = (ev: React.KeyboardEvent<HTMLInputElement>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendForm();
        }
    };

    const spinnerStyles: ISpinnerStyles = {
        circle: {
            borderColor: "#019dcd #019dcd #c2f1ff00 #c2f1ff00",
            height: "16px",
            width: "16px",
            borderWidth: "2px",
            borderStyle: "solid"
        }
    };

    const removeFile = (index: number) => {
        appStateContext?.dispatch({ type: "REMOVE_FILE", payload: index });
        fileInputRef?.current && (fileInputRef.current.value = "");
    };

    return (
        <div
            className={
                "transition-[padding] duration-200 text-base m-auto pl-[20px] pr-[29px] " +
                (appStateContext?.state.isChatHistoryOpen ? "" : "lg:pl-[20px] lg:pr-[29px]")
            }
        >
            {showToEnd && (
                <div
                    className="absolute left-0 right-0 z-[4] mx-auto w-[32px] h-[32px] animate-bounce rounded-full bg-jtc-white outline outline-2 outline-jtc-purple hover:outline-jtc-blue"
                    style={{ bottom: 99 }}
                >
                    <button
                        className="flex justify-center items-center h-[32px] w-[32px] font-semibold text-jtc-purple hover:text-jtc-blue"
                        onClick={toEnd}
                    >
                        <span className="material-symbols-outlined text-[1.25rem]">keyboard_arrow_down</span>
                    </button>
                </div>
            )}

            <div className="mx-auto flex flex-1 text-base gap-4 lg:gap-6 lg:max-w-chat-composer">
                <div className="flex w-full items-center">
                    {appStateContext?.state.assistant?.assistant === "sp" && windowWidth >= 976 && (
                        <a
                            className="mr-6 max-w-sm min-w-72 flex-grow bg-jtc-white hover:text-jtc-white flex items-center justify-center h-[59px] rounded-lg"
                            href="https://joogle.jtcgroup.com/shared-ownership/tax-guidance/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ flexBasis: "60%" }}
                        >
                            <div className="tracking-[0.15rem] text-jtc-purple hover:text-jtc-blue cursor-pointer font-semibold">
                                Click here for tax guidance
                            </div>
                        </a>
                    )}

                    <div className="overflow-hidden flex flex-col w-full relative rounded-lg bg-jtc-grey pl-4 pr-14 pb-4 pt-4 gap-2">
                        {appStateContext?.state.assistant?.attachments &&
                            appStateContext?.state.files?.map((fileItem, index) =>
                                fileItem.new ? (
                                    <a key={index} className="gap-2 w-fit flex items-center">
                                        {fileItem.status === "Loading" && (
                                            <span className="flex justify-center items-center h-[24px] w-[24px]">
                                                <Spinner style={{ display: "block", verticalAlign: "middle" }} styles={spinnerStyles} />
                                            </span>
                                        )}
                                        <span
                                            className={fileItem.status === "Error" ? "text-jtc-disabled" : "text-jtc-black"}
                                            title={fileItem.status === "Error" ? fileItem.error : undefined}
                                        >
                                            {fileItem.status === "Error"
                                                ? `${fileItem.file.name}: ${fileItem.error ?? "Unable to upload"}`
                                                : fileItem.file.name}
                                        </span>
                                        <ButtonTooltip title="Remove file" placement="top">
                                            <button className="flex justify-center font-semibold text-jtc-blue" onClick={() => removeFile(index)}>
                                                <span className="material-symbols-outlined text-[1.25rem]">cancel</span>
                                            </button>
                                        </ButtonTooltip>
                                    </a>
                                ) : null
                            )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <input
                                type="text"
                                placeholder="Full name (include any middle names)"
                                className="h-[40px] rounded p-2 text-jtc-black"
                                value={fullName}
                                onChange={e => setFullName(e.target.value)}
                            />

                            <select
                                className="h-[40px] rounded p-2 text-jtc-black"
                                value={gender}
                                onChange={e => setGender(e.target.value)}
                            >
                                <option value="">Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                            </select>

                            <input
                                type="date"
                                placeholder="Date of birth"
                                className="h-[40px] rounded p-2 text-jtc-black"
                                value={dob}
                                onChange={e => setDob(e.target.value)}
                            />

                            <select
                                className="h-[40px] rounded p-2 text-jtc-black"
                                value={countryLocation}
                                onChange={e => setCountryLocation(e.target.value)}
                            >
                                <option value="">Country location</option>
                                {isoCountries.map(c => (
                                    <option key={c.code} value={c.code}>{`${c.name} (${c.code})`}</option>
                                ))}
                            </select>

                            <select
                                className="h-[40px] rounded p-2 text-jtc-black"
                                value={placeOfBirth}
                                onChange={e => setPlaceOfBirth(e.target.value)}
                            >
                                <option value="">Place of birth</option>
                                {isoCountries.map(c => (
                                    <option key={c.code} value={c.code}>{`${c.name} (${c.code})`}</option>
                                ))}
                            </select>

                            <select
                                className="h-[40px] rounded p-2 text-jtc-black"
                                value={nationalityIso3}
                                onChange={e => setNationalityIso3(e.target.value)}
                            >
                                <option value="">Nationality</option>
                                {isoCountries.map(c => (
                                    <option key={c.code} value={c.code}>{`${c.name} (${c.code})`}</option>
                                ))}
                            </select>

                            <input
                                type="text"
                                placeholder="Registered country (organisation)"
                                className="h-[40px] rounded p-2 text-jtc-black"
                                value={regCountryOrg}
                                onChange={e => setRegCountryOrg(e.target.value)}
                            />

                            <input
                                type="text"
                                inputMode="numeric"
                                placeholder="IMO number (vessel)"
                                className="h-[40px] rounded p-2 text-jtc-black"
                                value={imoNumber}
                                onChange={e => setImoNumber(e.target.value.replace(/\D/g, ""))} // fix non-digit input
                                onKeyDown={onEnterPress}
                            />

                            <label className="flex items-center gap-2 md:col-span-2">
                                <input
                                    type="checkbox"
                                    checked={mediaCheck}
                                    onChange={e => setMediaCheck(e.target.checked)} // toggle media check
                                />
                                <span className="text-jtc-black">Add media check</span>
                            </label>
                        </div>

                        <button
                            disabled={sendDisabled}
                            onClick={sendForm}
                            type="submit"
                            className="absolute right-3 bottom-[11.5px] z-50 h-8 w-8 rounded-lg disabled:bg-jtc-disabled bg-jtc-purple transition duration-200 flex items-center justify-center"
                        >
                            <motion.svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-jtc-white h-4 w-4"
                            >
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <motion.path
                                    d="M5 12l14 0"
                                    initial={{ strokeDasharray: "50%", strokeDashoffset: "50%" }}
                                    animate={{ strokeDashoffset: !sendDisabled ? 0 : "50%" }}
                                    transition={{ duration: 0.3, ease: "linear" }}
                                />
                                <path d="M13 18l6 -6" />
                                <path d="M13 6l6 6" />
                            </motion.svg>
                        </button>

                        {formEmpty && chatEmpty && !disabled && (
                            <div className="absolute flex items-center pointer-events-none h-[59px] bottom-0 left-0 right-0">
                                <AnimatePresence mode="wait">
                                    <motion.p
                                        initial={{ y: 5, opacity: 0 }}
                                        key={`current-placeholder-${currentPlaceholder}`}
                                        animate={{ y: 0, opacity: 1 }}
                                        exit={{ y: -15, opacity: 0 }}
                                        transition={{ duration: 0.3, ease: "linear" }}
                                        className="text-left w-[calc(100%-2rem)] truncate text-[#7F7F7F] pl-4"
                                    >
                                        {placeholders[currentPlaceholder]}
                                    </motion.p>
                                </AnimatePresence>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="relative px-2 py-2 text-center text-[0.667rem] text-jtc-white leading-normal">
                <span className="lg:hidden inline-block">
                    JTC accepts no liability for the accuracy, completeness or use of ChatJTC's responses.
                </span>
                <span className="hidden lg:inline-block lg:max-w-chat-composer">
                    ChatJTC, powered by OpenAI, provides information and assistance for JTC users but cannot guarantee accuracy, completeness, or non-bias, so users should verify responses with reliable sources. JTC accepts no liability for the accuracy, completeness or use of ChatJTC's responses.
                </span>
            </div>
        </div>
    );
};
